import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import {
  BadgeCheck,
  Building2,
  Camera,
  KeyRound,
  Mail,
  Phone,
  Save,
  ShieldCheck,
  UserRound,
} from "lucide-react";
import AuthContext from "../../../../utils/secure-route/AuthContext";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import { useToastHandler } from "../../../common/ToastHandler";
import "./Settings.css";

const getProfileValue = (profile, keys, fallback = "") => {
  for (const key of keys) {
    const value = profile?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return fallback;
};

function MyProfile() {
  const { user } = useContext(AuthContext);
  const { Get, Patch, PatchFormData, _Get, getAPI } = useApi();
  const { handleToast } = useToastHandler();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState({});
  const [profileImage, setProfileImage] = useState("");
  const [profileImageFile, setProfileImageFile] = useState(null);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    contact_no: "",
  });

  const profileId = user?.user_id || user?.id;

  const resolveMediaUrl = (value) => {
    if (!value) return "";
    if (typeof value !== "string") return "";
    if (value.startsWith("blob:") || value.startsWith("data:")) return value;
    if (value.startsWith("http")) return value;
    return `${getAPI("path")}${value}`;
  };

  const firstFilled = (...values) =>
    values.find((value) => value !== undefined && value !== null && value !== "") || "";

  const displayName = useMemo(() => {
    const name = `${formData.first_name || ""} ${formData.last_name || ""}`.trim();
    return name || formData.email || "My Profile";
  }, [formData.email, formData.first_name, formData.last_name]);

  const initials = useMemo(() => {
    const parts = displayName.split(" ").filter(Boolean);
    return parts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join("") || "PV";
  }, [displayName]);

  const hydrateForm = (profileData = {}) => {
    const nextImage = resolveMediaUrl(
      firstFilled(
        profileData.profile_image,
        profileData.avatar,
        profileData.profile_picture,
        profileData.logo,
      ),
    );

    setProfile(profileData);
    setProfileImage(nextImage);
    setProfileImageFile(null);
    setFormData({
      first_name: getProfileValue(
        profileData,
        ["first_name", "firstName", "user_first_name"],
        user?.first_name || "",
      ),
      last_name: getProfileValue(
        profileData,
        ["last_name", "lastName", "user_last_name"],
        user?.last_name || "",
      ),
      email: getProfileValue(
        profileData,
        ["email", "username", "user_email", "store_email"],
        user?.email || user?.username || "",
      ),
      contact_no: getProfileValue(
        profileData,
        ["contact_no", "phoneNumber", "phone_number", "phone"],
        user?.contact_no || "",
      ),
    });
  };

  const mergeProfileSources = (accountData = {}, storeData = {}) => ({
    id: firstFilled(accountData.id, storeData.id, profileId),
    first_name: firstFilled(accountData.first_name, storeData.first_name, user?.first_name),
    last_name: firstFilled(accountData.last_name, storeData.last_name, user?.last_name),
    email: firstFilled(
      accountData.email,
      accountData.username,
      storeData.email,
      storeData.user_email,
      storeData.store_email,
      user?.email,
      user?.username,
    ),
    contact_no: firstFilled(
      accountData.contact_no,
      storeData.contact_no,
      storeData.phone_number,
      user?.contact_no,
    ),
    role_name: firstFilled(
      accountData.ms_role_name,
      accountData.role_name,
      storeData.role_name,
      storeData.ms_role_name,
      user?.role_name,
    ),
    store_name: firstFilled(
      accountData.selected_store_name,
      accountData.store_name,
      storeData.store_name,
      user?.store_name,
    ),
    subscription_plan: firstFilled(accountData.subscription_plan, storeData.subscription_plan),
    logo: firstFilled(accountData.logo, storeData.logo),
    digital_signature: firstFilled(accountData.digital_signature, storeData.digital_signature),
  });

  const fetchProfile = async () => {
    if (!profileId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [accountProfile, storeUserProfile] = await Promise.allSettled([
        Get(`${getAPI("updateUserProfile")}${profileId}`),
        _Get("checkUserType"),
      ]);

      const accountData =
        accountProfile.status === "fulfilled" ? accountProfile.value : {};
      const storeData =
        storeUserProfile.status === "fulfilled" ? storeUserProfile.value : {};

      hydrateForm(mergeProfileSources(accountData, storeData));
    } catch (error) {
      console.error("Failed to load profile:", error);
      handleToast({
        type: "ERROR",
        message: "Profile details could not be loaded.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileId]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setProfileImageFile(file);
    setProfileImage(URL.createObjectURL(file));
  };

  const clearProfileCaches = () => {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("cache_") && key.includes("checkUserType")) {
        localStorage.removeItem(key);
      }
      if (key.startsWith("cache_") && key.includes("user")) {
        localStorage.removeItem(key);
      }
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!profileId) {
      handleToast({ type: "ERROR", message: "User profile id was not found." });
      return;
    }

    const payload = {
      first_name: formData.first_name.trim(),
      last_name: formData.last_name.trim(),
    };
    const contactNo = String(formData.contact_no || "").trim();
    if (contactNo) {
      payload.contact_no = contactNo;
    }

    try {
      setSaving(true);

      await Patch("updateUserProfile", profileId, payload);

      if (profileImageFile) {
        const imagePayload = new FormData();
        imagePayload.append("logo", profileImageFile);
        await PatchFormData("updateUserProfile", profileId, imagePayload);
      }

      clearProfileCaches();
      window.dispatchEvent(
        new CustomEvent("paisavara-profile-updated", {
          detail: {
            ...profile,
            ...payload,
            email: formData.email,
            logo: profileImage,
          },
        }),
      );
      handleToast({
        type: "SUCCESS",
        message: "Profile updated successfully.",
      });
      await fetchProfile();
    } catch (error) {
      console.error("Failed to update profile:", error);
      handleToast({
        type: "ERROR",
        message:
          error.response?.data?.message ||
          error.response?.data?.detail ||
          "Profile could not be updated.",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="responsive-box my-profile-workspace">
      <ToastContainer />
      <div className="settings-hero my-profile-hero">
        <div>
          <span className="settings-eyebrow">
            <UserRound size={16} />
            Account center
          </span>
          <h1>My Profile</h1>
          <p>
            Keep your account details current. These details are used across
            your PaisaVara workspace and store activity.
          </p>
        </div>
        <Link to="/change-password" className="settings-secondary-action">
          <KeyRound size={18} />
          Change Password
        </Link>
      </div>

      <div className="my-profile-grid">
        <aside className="my-profile-summary">
          <div className="my-profile-avatar-wrap">
            {profileImage ? (
              <img src={profileImage} alt={displayName} className="my-profile-avatar-img" />
            ) : (
              <div className="my-profile-avatar">{initials}</div>
            )}
            <label className="my-profile-image-action">
              <Camera size={16} />
              <span>Upload photo</span>
              <input type="file" accept="image/*" onChange={handleImageChange} />
            </label>
          </div>
          <h2>{displayName}</h2>
          <p>{formData.email}</p>

          <div className="my-profile-meta-card">
            <span>
              <ShieldCheck size={18} />
              Role
            </span>
            <strong>{profile?.role_name || user?.role_name || "User"}</strong>
          </div>
          <div className="my-profile-meta-card">
            <span>
              <Building2 size={18} />
              Store
            </span>
            <strong>{profile?.store_name || "Current store"}</strong>
          </div>
          <div className="my-profile-meta-card">
            <span>
              <BadgeCheck size={18} />
              Subscription
            </span>
            <strong>{profile?.subscription_plan || "Active workspace"}</strong>
          </div>
        </aside>

        <section className="my-profile-panel">
          <div className="my-profile-panel-header">
            <div>
              <h2>Account Details</h2>
              <p>Update your name and phone number.</p>
            </div>
          </div>

          {loading ? (
            <div className="my-profile-loading">Loading profile...</div>
          ) : (
            <form className="my-profile-form" onSubmit={handleSubmit}>
              <div className="my-profile-field-grid">
                <label className="my-profile-field">
                  <span>First Name</span>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    placeholder="Enter first name"
                  />
                </label>

                <label className="my-profile-field">
                  <span>Last Name</span>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    placeholder="Enter last name"
                  />
                </label>

                <label className="my-profile-field">
                  <span>
                    <Mail size={16} />
                    Email Address
                  </span>
                  <input type="email" value={formData.email} disabled />
                  <small>Email is used for login and cannot be changed here.</small>
                </label>

                <label className="my-profile-field">
                  <span>
                    <Phone size={16} />
                    Phone Number
                  </span>
                  <input
                    type="tel"
                    name="contact_no"
                    value={formData.contact_no}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                  />
                </label>
              </div>

              <div className="my-profile-actions">
                <button
                  type="submit"
                  className="settings-primary-action"
                  disabled={saving}
                >
                  <Save size={18} />
                  {saving ? "Saving..." : "Save Profile"}
                </button>
              </div>
            </form>
          )}
        </section>
      </div>
    </div>
  );
}

export default MyProfile;
